<template>
  <div>
    <div class="column">
      <div class="col q-pb-sm text-grey-brand text-bold">
        User Notifications
      </div>
      <div class="col q-pb-sm">
        <q-btn
          dense
          size="sm"
          class="btn-brand"
          icon="fas fa-plus"
          label="Add User Notification"
          no-caps
          @click="onAddUserNotification"
        />&nbsp;
      </div>
      <div v-if="listError" class="col q-pb-sm text-negative">
        {{ listError }}
      </div>
      <div v-if="usersError" class="col q-pb-sm text-negative">
        {{ usersError }}
      </div>
      <div class="col q-pb-sm">
        <q-table
          v-model:pagination="infinitePagination"
          dense
          flat
          title=""
          :rows="notificationTable"
          :columns="userNotificationColumns"
          row-key="userId"
          no-data-label="No user notifications added"
          hide-pagination
          :visible-columns="userNotificationVisibleColumns"
        >
          <template #body-cell-name="props">
            <q-td :props="props">
              {{ notificationRowName(props.row) }}
              <q-badge
                v-if="rowSmsNotOptedIn(props.row)"
                color="warning"
                label="SMS (not opted in)"
                class="q-ml-xs"
                outline
              >
                <q-tooltip>{{ smsNotOptedInSelectedMessage }}</q-tooltip>
              </q-badge>
            </q-td>
          </template>
          <template #body-cell-info-user="props">
            <q-td :props="props">
              <q-btn
                class="btn-brand"
                size="xs"
                dense
                icon="fas fa-info-circle"
                @click="onUserNotificationInfo(props)"
              ></q-btn>
            </q-td>
          </template>
          <template #body-cell-delete-user="props">
            <q-td :props="props">
              <q-btn
                class="btn-brand"
                size="xs"
                dense
                icon="fas fa-trash"
                :loading="deletingUserId === props.row.userId"
                :disable="saving || deletingUserId !== null"
                @click="onDeleteUserNotification(props)"
              ></q-btn>
            </q-td>
          </template>
        </q-table>
      </div>
    </div>

    <q-dialog v-model="infoNotifierDialog">
      <q-card style="width: 350px; max-width: 90vw">
        <q-toolbar>
          <div class="text-blue-brand text-bold">User Notification</div>
          <q-space />
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>
        <q-card-section v-if="selectedUserNotification">
          <div class="q-pa-sm">
            User: {{ notificationRowName(selectedUserNotification) }}
          </div>
          <div class="q-pa-sm">
            Launch: {{ selectedUserNotification.launch }}
          </div>
          <div class="q-pa-sm">
            Receive: {{ selectedUserNotification.receive }}
          </div>
          <div class="q-pa-sm">
            Medium: {{ selectedUserNotification.medium }}
          </div>
          <div
            v-if="rowSmsNotOptedIn(selectedUserNotification)"
            class="q-px-sm q-pb-sm text-caption text-grey-8"
          >
            <q-icon name="info" color="warning" class="q-mr-xs" />
            {{ smsNotOptedInSelectedMessage }}
          </div>
          <div class="q-pa-sm">
            Passage Type: {{ selectedUserNotification.passageType }}
          </div>
          <div class="q-pa-sm">
            Include Manual: {{ selectedUserNotification.manualPassage }}
          </div>
          <div class="q-pa-sm">
            All Markers: {{ selectedUserNotification.allMarkers }}
          </div>
          <div v-if="!selectedUserNotification.allMarkers" class="q-pa-sm">
            Markers: {{ selectedUserNotification.markers }}
          </div>
          <q-separator />
          <q-card-actions align="center">
            <q-btn v-close-popup class="btn-brand" label="Close" no-caps />
          </q-card-actions>
        </q-card-section>
      </q-card>
    </q-dialog>

    <q-dialog v-model="addNotifierDialog" :persistent="saving">
      <q-card style="width: 350px; max-width: 90vw">
        <q-toolbar>
          <div class="text-blue-brand text-bold">Add User Notification</div>
          <q-space />
          <q-btn
            v-close-popup
            flat
            round
            dense
            icon="close"
            :disable="saving"
          />
        </q-toolbar>
        <q-card-section>
          <div class="q-pb-sm">
            <q-select
              v-model="selectedUser"
              dense
              :options="userOptions"
              color="blue-brand"
              label="User"
              filled
              :loading="usersLoading"
              :disable="usersLoading"
              @update:model-value="onSelectedUserChanged"
            />
            <div v-if="usersError" class="q-pt-xs text-caption text-negative">
              {{ usersError }}
            </div>
          </div>
          <div class="q-pa-sm">
            <div class="q-mb-md">Notification Settings</div>

            <q-card flat bordered>
              <q-radio
                v-model="medium"
                color="blue-brand"
                val="sms"
                label="SMS"
                :disable="!selectedUserCanUseSms"
              />
              <q-radio
                v-model="medium"
                color="blue-brand"
                val="email"
                label="E-mail"
              />
              <q-radio
                v-model="medium"
                color="blue-brand"
                val="both"
                label="Both"
                :disable="!selectedUserCanUseSms"
              />
              <q-radio
                v-model="medium"
                color="blue-brand"
                val="none"
                label="None"
                :disable="!selectedUserCanUseSms"
              />
            </q-card>
            <div
              v-if="!selectedUserCanUseSms"
              class="q-pt-sm text-caption text-grey-8"
            >
              <q-icon name="info" color="warning" class="q-mr-xs" />
              {{ smsNotOptedInMessage }}
            </div>

            <q-card flat bordered class="q-mt-md">
              <q-checkbox
                v-model="launch"
                color="blue-brand"
                label="At Launch"
              /><br />
              <q-checkbox
                v-model="receive"
                color="blue-brand"
                label="At Receive"
              /><br />
            </q-card>

            <q-card flat bordered class="q-mt-md">
              <q-radio
                v-model="passageType"
                color="blue-brand"
                val="verified"
                label="Verified Passages"
              /><br />
              <q-radio
                v-model="passageType"
                color="blue-brand"
                val="passage"
                label="All Passages (excluding manual)"
              /><br />
              <q-checkbox
                v-model="manualPassages"
                color="blue-brand"
                label="Include Manual Passages"
              /><br />
            </q-card>

            <q-card flat bordered class="q-mt-md">
              <q-checkbox
                v-model="allMarkers"
                color="blue-brand"
                label="All Markers"
              />
              <q-select
                v-if="!allMarkers"
                v-model="tmpSelectedMarkers"
                dense
                filled
                color="blue-brand"
                multiple
                :options="markerOptions"
                use-chips
                stack-label
                label="Markers"
                style="width: 300px"
              />
            </q-card>
          </div>

          <div v-if="addError" class="q-px-sm q-pb-sm text-negative">
            {{ addError }}
          </div>

          <q-card-actions align="center">
            <q-btn
              v-close-popup
              class="btn-brand"
              label="Cancel"
              no-caps
              :disable="saving"
            />
            <q-btn
              :disable="selectedUser == '' || !canSaveSelectedMedium"
              :loading="saving"
              class="btn-brand"
              label="Save"
              no-caps
              @click="onUserNotificationAdded"
            />
          </q-card-actions>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { UserNotification } from '@/models/UserNotification';
import UserService from '@/services/UserService';
import {
  SMS_NOT_OPTED_IN_MESSAGE,
  SMS_NOT_OPTED_IN_SELECTED_MESSAGE,
  NON_SMS_FALLBACK_MEDIUM,
  canSaveMediumForUser,
  canUseSmsForUser,
  isSmsBlockedForEntry,
  mediumSendsSms,
  smsOptInForUser,
  withSmsOptInStatus,
} from '@/utils/SmsOptIn';
import {
  UserPickerOption,
  activeUsers,
  userPickerOptions,
} from '@/utils/UserExpiry';
import {
  DELETE_USER_NOTIFICATION_FAILED,
  removeUserNotification,
  SAVE_USER_NOTIFICATION_FAILED,
  SaveUserNotifications,
  SaveUserNotificationsResult,
  saveUserNotificationList,
  upsertUserNotification,
} from '@/utils/UserNotificationList';

export default defineComponent({
  name: 'SmsNotifications',
  props: {
    markers: { type: Array, default: () => [] },
    notifications: { type: Array, default: () => [] },
    // EN-1570: saves the whole list. When set, Add and Delete save straight
    // away (one Save), and the list only changes once the save succeeds.
    saveNotifications: {
      type: Function as PropType<SaveUserNotifications>,
      default: null,
    },
  },
  emits: ['update-notifications'],
  data() {
    return {
      addNotifierDialog: false,
      infoNotifierDialog: false,
      saving: false,
      deletingUserId: null as string | null,
      addError: '',
      listError: '',
      launch: true,
      receive: true,
      medium: 'sms',
      passageType: 'verified',
      manualPassages: true,
      allMarkers: true,
      tmpSelectedMarkers: [] as string[],
      markerOptions: [] as string[],
      selectedUser: '' as any,
      // EN-15: users from GET /user, with their SMS opt-in state.
      // EN-1883: every user, inactive ones too (rows keep their opt-in
      // status); userOptions offers only the active ones.
      notificationUsers: [] as UserPickerOption[],
      usersLoading: false,
      usersLoaded: false,
      usersError: '',
      smsNotOptedInMessage: SMS_NOT_OPTED_IN_MESSAGE,
      smsNotOptedInSelectedMessage: SMS_NOT_OPTED_IN_SELECTED_MESSAGE,
      selectedUserNotification: null as any,
      userNotifications: [] as UserNotification[],
      infinitePagination: { rowsPerPage: 0 },
      userNotificationVisibleColumns: ['name', 'info-user', 'delete-user'],
      userNotificationColumns: [
        { name: 'name', label: 'Name', field: 'name', sortable: false },
        { name: 'userId', label: 'UserID', field: 'userId', sortable: false },
        { name: 'launch', label: 'Launch', field: 'launch', sortable: false },
        {
          name: 'receive',
          label: 'Receive',
          field: 'receive',
          sortable: false,
        },
        {
          name: 'passageType',
          label: 'Passage Type',
          field: 'passageType',
          sortable: false,
        },
        {
          name: 'manualPassage',
          label: 'Manual Passage',
          field: 'manualPassage',
          sortable: false,
        },
        {
          name: 'allMarkers',
          label: 'All Markers',
          field: 'allMarkers',
          sortable: false,
        },
        {
          name: 'markers',
          label: 'Markers',
          field: 'markers',
          sortable: false,
        },
        { name: 'info-user', field: 'user', label: '' },
        { name: 'delete-user', field: 'user', label: '' },
      ],
    };
  },
  computed: {
    // EN-15: label is "Name - Opted In" / "Name - Opted Out".
    // EN-1883: deactivated / expired users cannot be picked.
    userOptions(): UserPickerOption[] {
      return activeUsers(this.notificationUsers);
    },
    // EN-15: SMS is only offered to users who have opted in to SMS.
    selectedUserCanUseSms(): boolean {
      if (!this.selectedUser) return true;
      return canUseSmsForUser(this.notificationUsers, this.selectedUser.value);
    },
    canSaveSelectedMedium(): boolean {
      // 'None' is saved as SMS, so it counts as SMS here (see SmsOptIn.ts).
      return canSaveMediumForUser(this.selectedUserCanUseSms, this.medium);
    },
    notificationTable(): UserNotification[] {
      return this.userNotifications;
    },
  },
  watch: {
    notifications: {
      immediate: true,
      deep: true,
      handler(value: UserNotification[]) {
        this.userNotifications = value ? [...value] : [];
      },
    },
  },
  mounted() {
    this.loadNotificationUsers();
  },
  methods: {
    loadNotificationUsers() {
      this.usersLoading = true;
      this.usersLoaded = false;
      this.usersError = '';
      UserService.getUsers()
        .then((response: any) => {
          this.notificationUsers = userPickerOptions(response.data);
          this.usersLoaded = true;
        })
        .catch((error: any) => {
          console.error('Failed to fetch users:', error);
          this.notificationUsers = [];
          this.usersError =
            'Could not load users. Close and reopen Notifications to try again.';
        })
        .finally(() => {
          this.usersLoading = false;
        });
    },
    // A user who has not opted in cannot be given SMS: fall back to E-mail.
    onSelectedUserChanged() {
      if (!this.selectedUserCanUseSms && mediumSendsSms(this.medium)) {
        this.medium = NON_SMS_FALLBACK_MEDIUM;
      }
    },
    // Display only: the saved name, suffixed with the current opt-in status.
    notificationRowName(row: UserNotification | null): string {
      if (!row) return '';
      return withSmsOptInStatus(
        row.name,
        smsOptInForUser(this.notificationUsers, row.userId)
      );
    },
    // An existing SMS entry for a user who cannot use SMS, by the wizard's
    // rule. Only once users loaded: until then every user is unknown.
    rowSmsNotOptedIn(row: UserNotification | null): boolean {
      if (!row || !this.usersLoaded) return false;
      return isSmsBlockedForEntry(
        this.notificationUsers,
        row.userId,
        row.medium
      );
    },
    onAddUserNotification() {
      this.addError = '';
      this.listError = '';
      this.selectedUser = '';
      this.passageType = 'verified';
      this.launch = true;
      this.receive = true;
      this.medium = 'sms';
      this.manualPassages = true;
      this.allMarkers = true;
      this.tmpSelectedMarkers = [];

      this.markerOptions = [];
      if (this.markers) {
        this.markerOptions = (this.markers as any[]).map((m: any) => m.name);
      }

      this.addNotifierDialog = true;
    },
    async onUserNotificationAdded() {
      if (!this.selectedUser || this.saving) return;
      if (!this.canSaveSelectedMedium) return;

      // A user has one entry; adding them again replaces it.
      const next = upsertUserNotification(
        this.userNotifications,
        new UserNotification(
          // EN-15: save the plain name, not the "- Opted In" display label.
          this.selectedUser.name ?? this.selectedUser.label,
          this.selectedUser.value,
          this.launch,
          this.receive,
          this.medium,
          this.passageType,
          this.manualPassages,
          this.allMarkers,
          this.allMarkers ? undefined : this.tmpSelectedMarkers
        )
      );

      if (await this.persist(next, 'add')) {
        this.addNotifierDialog = false;
      }
    },
    async onDeleteUserNotification(props: any) {
      if (this.saving || this.deletingUserId !== null) return;
      const next = removeUserNotification(
        this.userNotifications,
        props.row.userId
      );
      this.deletingUserId = props.row.userId;
      try {
        await this.persist(next, 'delete');
      } finally {
        this.deletingUserId = null;
      }
    },
    // Saves `next` (when the parent gave saveNotifications) and only then
    // shows it. On failure the list is unchanged and an error is shown.
    async persist(
      next: UserNotification[],
      action: 'add' | 'delete'
    ): Promise<boolean> {
      this.addError = '';
      this.listError = '';
      this.saving = true;
      let result: SaveUserNotificationsResult;
      try {
        result = await saveUserNotificationList(
          next,
          this.saveNotifications,
          action === 'add'
            ? SAVE_USER_NOTIFICATION_FAILED
            : DELETE_USER_NOTIFICATION_FAILED
        );
      } finally {
        this.saving = false;
      }
      if (!result.ok) {
        if (action === 'add') this.addError = result.error;
        else this.listError = result.error;
        return false;
      }
      this.userNotifications = result.list;
      this.$emit('update-notifications', this.userNotifications);
      return true;
    },
    onUserNotificationInfo(props: any) {
      this.selectedUserNotification = props.row;
      this.infoNotifierDialog = true;
    },
  },
});
</script>

<style></style>
